import {
  Briefcase,
  CalendarPlus,
  Gift,
  Heart,
  ListChecks,
  Lock,
  Microphone,
  PushPin,
  ShoppingCart,
  Signpost,
  Sparkle,
  Ticket,
  UsersThree,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Avatar, Button, Icon, Sheet, Text, cn } from '@shh/ui';
import { Suspense, lazy, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { EventForm } from '../components/EventForm';
import { PlanForm } from '../components/PlanForm';
import { NoteForm, RewardForm } from '../components/forms';
import { JobForm } from '../components/taskForms';
import { IdeaForm, VoucherForm, WishForm } from '../components/leisureForms';
import { FAMILY_WISHES } from '@shh/shared';
import { KudosSheet } from '../components/Rewards';
// loaded when "Einkauf" is picked – keeps the main file under the offline cache's 2 MB
const ShoppingList = lazy(() =>
  import('../components/Shopping').then((m) => ({ default: m.ShoppingList })),
);
import { MagicAdd } from '../components/MagicAdd';
import { addDay } from '../lib/addDay';
import { kids, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { useLens } from './lens';
import { addText } from '../i18n/add';

type Kind =
  | 'magic'
  | 'event'
  | 'shift'
  | 'job'
  | 'shopping'
  | 'note'
  | 'reward'
  | 'kudos'
  | 'idea'
  | 'voucher'
  | 'wish';

/**
 * Two levels (1.28, concept 24.10.): the four things a family adds every day as big tiles, the
 * six rarer ones as small rows below – all visible, nothing hidden behind "Mehr".
 */
type Level = 'daily' | 'more';

const OPTIONS: {
  id: Kind;
  icon: PhosphorIcon;
  title: string;
  text: string;
  level: Level;
  /** shopping = also kids and the wall, when the list is connected; all = everybody */
  who: 'parent' | 'planner' | 'shopping' | 'all';
}[] = [
  {
    id: 'magic',
    level: 'daily',
    icon: Sparkle,
    title: addText.options.magic.title,
    text: addText.options.magic.text,
    // AI only for parents – the wall with the PIN (Mike, 24.10.: "KI rechtssicher")
    who: 'parent',
  },
  {
    id: 'event',
    level: 'daily',
    icon: CalendarPlus,
    title: addText.options.event.title,
    text: addText.options.event.text,
    who: 'planner',
  },
  {
    // one entry for jobs (1.28): once or regular, with or without stars
    id: 'job',
    level: 'daily',
    icon: ListChecks,
    title: addText.options.job.title,
    text: addText.options.job.text,
    who: 'planner',
  },
  {
    id: 'shopping',
    level: 'daily',
    icon: ShoppingCart,
    title: addText.options.shopping.title,
    text: addText.options.shopping.text,
    who: 'shopping',
  },
  {
    id: 'note',
    level: 'daily',
    icon: PushPin,
    title: addText.options.note.title,
    text: addText.options.note.text,
    // everybody pins, also kids and the wall (Mike, 24.10.: "Notizen auch alle")
    who: 'all',
  },
  {
    id: 'shift',
    level: 'more',
    icon: Briefcase,
    title: addText.options.shift.title,
    text: addText.options.shift.text,
    who: 'planner',
  },
  {
    id: 'kudos',
    level: 'more',
    icon: Heart,
    title: addText.options.kudos.title,
    text: addText.options.kudos.text,
    who: 'parent',
  },
  {
    id: 'reward',
    level: 'more',
    icon: Gift,
    title: addText.options.reward.title,
    text: addText.options.reward.text,
    who: 'parent',
  },
  {
    id: 'idea',
    level: 'more',
    icon: Signpost,
    title: addText.options.idea.title,
    text: addText.options.idea.text,
    who: 'all',
  },
  {
    id: 'voucher',
    level: 'more',
    icon: Ticket,
    title: addText.options.voucher.title,
    text: addText.options.voucher.text,
    who: 'planner',
  },
  {
    id: 'wish',
    level: 'more',
    icon: Gift,
    title: addText.options.wish.title,
    text: addText.options.wish.text,
    who: 'all',
  },
];
const LEVELS: Level[] = ['daily', 'more'];

const TITLES: Record<Kind, string> = addText.titles;

/** "＋ Hinzufügen" – one entry point for everything new, from every page. */
export function AddSheet({
  isOpen,
  onOpenChange,
  onUnlock,
}: {
  isOpen: boolean;
  onOpenChange: (o: boolean) => void;
  /** the wall in the family mode: open the PIN for the parent mode */
  onUnlock?: () => void;
}) {
  const f = useFamily();
  const lens = useLens();
  const [kind, setKind] = useState<Kind | null>(null);
  const [kudosFor, setKudosFor] = useState<M | null>(null);
  // whose wish list: kids and teens their own, the others pick (or the family's)
  const [wishFor, setWishFor] = useState<string | null>(null);
  const canPlan = lens.canParent || f.role === 'teen';
  const options = OPTIONS.filter((o) =>
    o.who === 'parent' ? lens.canParent : o.who === 'all' || o.who === 'shopping' ? true : canPlan,
  );
  const magic = options.find((o) => o.id === 'magic');
  const others = options.filter((o) => o.id !== 'magic');
  const me = lens.me && lens.me.role !== 'parent' ? lens.me.id : null;
  const wishOwner = wishFor ?? me;
  const close = () => {
    setKind(null);
    setWishFor(null);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet
        isOpen={isOpen}
        onOpenChange={(o) => {
          if (!o) {
            setKind(null);
            setWishFor(null);
          }
          onOpenChange(o);
        }}
        title={kind ? TITLES[kind] : addText.ask}
        // a typed sentence is quickly typed again – "Einfach sagen" closes like the menu itself
        isDismissable={!kind || kind === 'magic'}
      >
        {!kind && (
          <div className="flex flex-col gap-gap-lg">
            {/* "Einfach sagen" can stand in for all the others – so it gets the big place on top
                (Mike, 07.10.); the rest stays below for whoever knows exactly what to add. */}
            {magic && (
              <AriaButton
                onPress={() => setKind('magic')}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-gap-md rounded-card bg-accent-primary-soft p-inset-md text-left outline-none',
                  'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-secondary-hover data-pressed:scale-97',
                )}
              >
                <span className="grid size-avatar-lg shrink-0 place-items-center rounded-card bg-action-primary text-text-on-action">
                  <Icon icon={Microphone} size="xl" weight="fill" />
                </span>
                <span className="flex flex-col gap-gap-xs">
                  <span className="flex items-center gap-gap-xs font-display text-lg font-black">
                    {magic.title} <Icon icon={Sparkle} size="sm" className="text-accent-primary" />
                  </span>
                  <span className="text-sm text-text-secondary">{magic.text}</span>
                  <span className="text-sm italic text-text-secondary">{addText.magicExample}</span>
                </span>
              </AriaButton>
            )}
            {magic && others.length > 0 && (
              <Text size="sm" tone="muted" className="-mb-gap-sm">
                {addText.orDirect}
              </Text>
            )}
            {others.length
              ? LEVELS.map((level) => {
                  const items = others.filter((o) => o.level === level);
                  if (!items.length) return null;
                  return level === 'daily' ? (
                    <ul
                      key={level}
                      aria-label={addText.levels.daily}
                      className="grid grid-cols-2 gap-gap-sm"
                    >
                      {items.map((o) => (
                        <li key={o.id}>
                          <AriaButton
                            onPress={() => setKind(o.id)}
                            className={cn(
                              'flex h-full w-full cursor-pointer flex-col items-start gap-gap-xs rounded-card bg-surface-sunken p-inset-sm text-left outline-none',
                              'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-secondary-hover data-pressed:scale-97',
                            )}
                          >
                            <Icon icon={o.icon} size="lg" className="text-accent-secondary" />
                            <span className="text-lg font-bold">{o.title}</span>
                            <span className="text-sm text-text-secondary">{o.text}</span>
                          </AriaButton>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul
                      key={level}
                      aria-label={addText.levels.more}
                      className="flex flex-col divide-y divide-border-subtle rounded-card border border-border-subtle"
                    >
                      {items.map((o) => (
                        <li key={o.id}>
                          <AriaButton
                            onPress={() => setKind(o.id)}
                            className={cn(
                              'flex min-h-touch-min w-full cursor-pointer items-center gap-gap-sm px-inset-sm py-inset-xs text-left outline-none',
                              'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover',
                            )}
                          >
                            <Icon
                              icon={o.icon}
                              size="md"
                              className="shrink-0 text-accent-secondary"
                            />
                            <span className="font-semibold">{o.title}</span>
                            <span className="min-w-0 flex-1 truncate text-sm text-text-secondary">
                              {o.text}
                            </span>
                          </AriaButton>
                        </li>
                      ))}
                    </ul>
                  );
                })
              : !magic && <Text>{addText.parentsAdd}</Text>}
            {/* hidden here, one tap to the PIN (Mike, 24.10.: "an den relevanten Stellen … auch beim +Menü") */}
            {lens.isWall && !lens.canParent && onUnlock && (
              <Button variant="quiet" icon={Lock} onPress={onUnlock} className="self-start">
                {addText.wallHint}
              </Button>
            )}
          </div>
        )}
        {kind === 'magic' && <MagicAdd onDone={close} />}
        {kind === 'event' && (
          <EventForm draft={addDay() ? { date: addDay() } : undefined} onDone={close} />
        )}
        {kind === 'shift' && <PlanForm onDone={close} />}
        {kind === 'job' && <JobForm onDone={close} />}
        {kind === 'shopping' && (
          <div className="flex flex-col gap-gap-lg">
            <Suspense fallback={null}>
              <ShoppingList />
            </Suspense>
            <Button variant="secondary" className="self-end" onPress={close}>
              {addText.done}
            </Button>
          </div>
        )}
        {kind === 'note' && <NoteForm onDone={close} />}
        {kind === 'reward' && <RewardForm onDone={close} />}
        {kind === 'idea' && <IdeaForm onDone={close} />}
        {kind === 'voucher' && <VoucherForm onDone={close} />}
        {kind === 'wish' && wishOwner && <WishForm owner={wishOwner} onDone={close} />}
        {kind === 'wish' && !wishOwner && (
          <div className="flex flex-col gap-gap-md">
            <Text>{addText.whoseWishes}</Text>
            <ul className="grid grid-cols-3 gap-gap-md">
              {[...f.members, null].map((m) => (
                <li key={m?.id ?? FAMILY_WISHES}>
                  <AriaButton
                    onPress={() => setWishFor(m?.id ?? FAMILY_WISHES)}
                    className="flex w-full cursor-pointer flex-col items-center gap-gap-xs rounded-card p-inset-xs outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover"
                  >
                    {m ? (
                      <Avatar name={m.name} color={m.color} emoji={m.emoji} size="lg" decorative />
                    ) : (
                      <span className="grid size-avatar-lg place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
                        <Icon icon={UsersThree} size="lg" weight="fill" />
                      </span>
                    )}
                    <span className="font-bold">{m ? m.name : addText.family}</span>
                  </AriaButton>
                </li>
              ))}
            </ul>
          </div>
        )}
        {kind === 'kudos' && (
          <ul className="grid grid-cols-3 gap-gap-md">
            {kids(f).map((m) => (
              <li key={m.id}>
                <AriaButton
                  onPress={() => {
                    close();
                    setKudosFor(m);
                  }}
                  className="flex w-full cursor-pointer flex-col items-center gap-gap-xs rounded-card p-inset-xs outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover"
                >
                  <Avatar name={m.name} color={m.color} emoji={m.emoji} size="lg" decorative />
                  <span className="font-bold">{m.name}</span>
                </AriaButton>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
      <KudosSheet member={kudosFor} onClose={() => setKudosFor(null)} />
    </>
  );
}
